import React from 'react';
import { Clock } from 'lucide-react';

interface IdleWarningDialogProps {
  secondsLeft: number;
  onStay: () => void;
}

/**
 * Shown for the last minute before the 15-minute idle sign-out (OP#425). Only the button keeps the
 * session: the dialog appearing under a resting pointer makes Chrome fire a mousemove, so treating
 * any movement as "still here" let the warning dismiss itself and the sign-out never happened.
 */
const IdleWarningDialog: React.FC<IdleWarningDialogProps> = ({ secondsLeft, onStay }) => (
  <div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 print:hidden"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="idle-warning-title"
    aria-describedby="idle-warning-message"
  >
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full border border-black/[0.08] shadow-2xl">
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-black/[0.05] text-[#475569]">
            <Clock className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 id="idle-warning-title" className="text-lg font-bold text-[#0f172a]">Are you still there?</h3>
            <div id="idle-warning-message" className="mt-2 text-sm text-[#475569] leading-relaxed">
              For security, you'll be signed out in{' '}
              <span className="font-semibold text-[#0f172a]">{secondsLeft} second{secondsLeft === 1 ? '' : 's'}</span>{' '}
              because of inactivity.
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 px-6 py-4 border-t border-black/[0.08] bg-black/[0.02] rounded-b-2xl">
        <button
          onClick={onStay}
          autoFocus
          className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors bg-[#0f172a] hover:bg-[#1e293b] text-white"
        >
          Stay signed in
        </button>
      </div>
    </div>
  </div>
);

export default IdleWarningDialog;
